import type { Editor, JSONContent } from "@tiptap/core";
import type { Mark, Node } from "prosemirror-model";

type Attrs = Record<string, unknown>;

const targetKey = (type: string, attrs: Attrs | undefined) => {
	const resourcePath = attrs?.resourcePath;
	if (typeof resourcePath !== "string" || !resourcePath || typeof attrs?.href !== "string") return null;
	return `${type}|${resourcePath}|${String(attrs.hash ?? "")}`;
};

const currentHrefs = (doc: Node) => {
	const hrefs = new Map<string, string>();
	const remember = (type: string, attrs: Attrs) => {
		const key = targetKey(type, attrs);
		if (key && !hrefs.has(key)) hrefs.set(key, attrs.href as string);
	};
	doc.descendants((node) => {
		remember(node.type.name, node.attrs);
		node.marks.forEach((mark: Mark) => remember(mark.type.name, mark.attrs));
		return true;
	});
	return hrefs;
};

const withHref = (hrefs: Map<string, string>, type: string | undefined, attrs: Attrs | undefined) => {
	const key = type && targetKey(type, attrs);
	const href = key && hrefs.get(key);
	return href && href !== attrs?.href ? { ...attrs, href } : attrs;
};

const align = (json: JSONContent, hrefs: Map<string, string>): JSONContent => ({
	...json,
	...(json.attrs && { attrs: withHref(hrefs, json.type, json.attrs) }),
	...(json.marks && {
		marks: json.marks.map((mark) => ({ ...mark, attrs: withHref(hrefs, mark.type, mark.attrs) })),
	}),
	...(json.content && { content: json.content.map((child) => align(child, hrefs)) }),
});

const alignResourceHrefs = (baseline: JSONContent, editor: Editor): JSONContent =>
	align(baseline, currentHrefs(editor.state.doc));

export default alignResourceHrefs;
